import { ComponentType, lazy } from 'react'

const CHUNK_RELOAD_KEY = 'chunk-reload-at'
const CHUNK_RELOAD_COOLDOWN_MS = 10000

type ComponentKeys<M> = {
  [P in keyof M]: M[P] extends ComponentType<any> ? P : never
}[keyof M]

/**
 * React.lazy for a named export. The export name is checked against the module's real exports at
 * compile time. A failed chunk download (typically a stale tab after a deploy) is retried once and
 * then the page is reloaded once (guarded by sessionStorage so it cannot loop); if that also fails
 * the error propagates to the nearest error boundary.
 */
export const lazyNamed = <M extends Record<string, any>, K extends ComponentKeys<M>>(
  load: () => Promise<M>,
  name: K,
) =>
  lazy(async () => {
    let mod: M
    try {
      mod = await load()
    } catch {
      try {
        mod = await load()
      } catch (error) {
        try {
          const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
          if (Date.now() - last > CHUNK_RELOAD_COOLDOWN_MS) {
            window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
            window.location.reload()
            // Keep Suspense showing its fallback while the page reloads.
            return await new Promise<never>(() => undefined)
          }
        } catch {
          // sessionStorage unavailable: fall through to the error boundary
        }
        throw error
      }
    }
    return { default: mod[name] as M[K] & ComponentType<any> }
  })
